import React, { useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import { Host } from "../../../../App";

export default function FlexSchedulePage({ postList, onSchedule }) {
  const token = window.localStorage.getItem("acessToken");

  const [Tentative, setTentative] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [Cross_Parts, setCross_Parts] = useState([]);

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    Axios.get(`https://${Host}/api/employees_all/${token}`)
      .then((data) => setEmployees(data.data || []))
      .catch(() => setEmployees([]));
  }, [token]);

  useEffect(() => {
    Axios.get(`https://${Host}/api/Tentative_schedule/${token}`)
      .then((data) => setTentative(data.data))
      .catch(() => setTentative([]));
  }, [token]);

  useEffect(() => {
    Axios.get(`https://${Host}/Inventory/cross_part_rules`)
      .then(({ data }) => setCross_Parts(data?.rows || []))
      .catch(() => setCross_Parts([]));
  }, [token]);

  const printRef = useRef(null);
  const handlePrint = () => {
    if (!printRef.current) return;
    const win = window.open("", "", "height=900,width=1200");
    win.document.write("<html><head><title>Print -F Required By Date</title>");
    win.document.write('<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css"/>');
    win.document.write("</head><body>");
    win.document.write(printRef.current.innerHTML);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────────────────────
  const normalise = (s) => (s || "").replace(/\s+/g, " ").trim();

  const norm = (s) =>
    String(s || "")
      .replace(/[–—]/g, "-")
      .replace(/["\\]/g, "")
      .replace(/\s*-\s*([FRWSMfrwsm])\b/gi, "-$1")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();

  // Parse PO_Details "3/26/2026 - 45, 4/8/2026 - 45" → [{date, qty}] sorted asc
  const parsePoDetails = (poStr) => {
    if (!poStr) return [];
    return String(poStr)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .reduce((acc, seg) => {
        const m = seg.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*-\s*(-?\d+)/);
        if (m) {
          const [, mm, dd, yyyy, q] = m;
          const qty = Math.max(0, parseInt(q, 10));
          if (qty > 0)
            acc.push({
              date: `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`,
              qty,
            });
        }
        return acc;
      }, [])
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  const toLocalISO = (dLike) => {
    if (!dLike) return "";
    const s = String(dLike);
    let m = s.match(/(\d{4})[-/](\d{2})[-/](\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    const m2 = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m2) return `${m2[3]}-${m2[1].padStart(2, "0")}-${m2[2].padStart(2, "0")}`;
    const d = new Date(s);
    if (isNaN(+d)) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // BOM maps from cross_part_rules
  // ─────────────────────────────────────────────────────────────────────────────
  const kitParents = useMemo(() => {
    const set = new Set();
    (Cross_Parts || []).forEach((rule) => set.add(norm(rule.src_part_no)));
    return set;
  }, [Cross_Parts]);

  const bomMap = useMemo(() => {
    const map = {};
    (Cross_Parts || []).forEach((rule) => {
      const parentKey = norm(rule.src_part_no);
      if (!map[parentKey]) map[parentKey] = [];
      map[parentKey].push({
        compInternal: norm(rule.dst_r_internal_no),
        rawCompInternal: rule.dst_r_internal_no || "",
        mult: Number(rule.multiplier || 0),
        customer: norm(rule.customer || ""),
      });
    });
    return map;
  }, [Cross_Parts]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 1 — Future tentative (Date >= today) aggregated by internal part
  // ─────────────────────────────────────────────────────────────────────────────
  const futureTentativeByInternal = useMemo(() => {
    const map = {};
    (Tentative || []).forEach((t) => {
      const iso = toLocalISO(t.Date || t.date || t.poDate);
      if (!iso || iso < todayISO) return;
      const key = norm(
        t.Internal_Part_No || t.internal_part_no || t.internalPartNo || t.internal || ""
      );
      const qty = Math.max(0, Number(t.QTY ?? t.qty ?? 0) || 0);
      if (key && qty > 0) map[key] = (map[key] || 0) + qty;
    });
    return map;
  }, [Tentative, todayISO]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 2 — Parse postList into part-level data
  //
  //  For each postList row we read:
  //    sqlRequiredQty  — from Required_Ship_QTY_List "-F (N)"  ← SOURCE OF TRUTH
  //    sqlPending      — from Pending_Parts_List "-F (N)"      ← used for date walk only
  //    poDateEntries   — from PO_Details                       ← date placement
  // ─────────────────────────────────────────────────────────────────────────────

  // Parse "-F (N)" from Required_Ship_QTY_List
  const parseReqF = (str) => {
    const s = normalise(str || "");
    // Split safely on "), "
    const entries = s
      .split(/\)\s*,\s*/)
      .map((e, i, arr) => (i < arr.length - 1 ? e + ")" : e));

    for (const entry of entries) {
      // Match anything ending with -F before the qty parens
      const match = entry.match(/^(.*\s-F)\s*\(\s*(-?\d+)\s*\)$/i);
      if (match) {
        const internalPart = normalise(match[1]);
        const qty = parseInt(match[2], 10);
        if (/-F$/i.test(internalPart) && qty > 0)
          return { internalPart, qty };
      }
    }
    return null;
  };

  // Parse all pending -F entries from Pending_Parts_List
  // Returns map: normalisedInternalPart → pendingQty
  const parsePendingF = (str) => {
    const map = {};
    const s = normalise(str || "");
    const entries = s
      .split(/\)\s*,\s*/)
      .map((e, i, arr) => (i < arr.length - 1 ? e + ")" : e));

    entries.forEach((entry) => {
      // Strip optional "Main Part - " / "Sub Part - " prefix
      const stripped = entry.replace(/^[\w\s]+ Part\s*-\s*/i, "");
      const match = stripped.match(/^(.*\s-F)\s*\(\s*(-?\d+)\s*\)$/i);
      if (match) {
        const internalPart = normalise(match[1]);
        const qty = parseInt(match[2], 10);
        if (/-F$/i.test(internalPart))
          map[norm(internalPart)] = (map[norm(internalPart)] || 0) + qty;
      }
    });
    return map;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 3 — Direct -F shortage rows using the SAME PO walk as Rigid:
  //
  //   demandThreshold = totalPO - sqlRequiredQty
  //   Walk PO dates forward:
  //     • while demandThreshold >= poQty → fully covered, skip
  //     • once demandThreshold < poQty  → shortage starts here
  //
  //   pendingToSchedule = min(sqlRequired, max(0, sqlRequired - futureTent))
  //   (no unfinished for F parts — only rigid R parts have unfinished production)
  // ─────────────────────────────────────────────────────────────────────────────
  const directRowsByDate = useMemo(() => {
    const out = {}; // date → [row]

    (postList || []).forEach((row) => {
      const reqF = parseReqF(row.Required_Ship_QTY_List || "");
      if (!reqF || reqF.qty <= 0) return;

      const { internalPart, qty: sqlRequiredQty } = reqF;
      const pendingMap = parsePendingF(row.Pending_Parts_List || "");
      const sqlPending = pendingMap[norm(internalPart)] ?? 0;
      const poEntries = parsePoDetails(row.PO_Details);
      const totalPoQty = poEntries.reduce((s, x) => s + x.qty, 0);

      const futureTent = futureTentativeByInternal[norm(internalPart)] || 0;

      // HOW MANY to schedule — SQL required, reduced by future tentative only
      const pendingToSchedule = Math.min(
        sqlRequiredQty,
        Math.max(0, sqlRequiredQty - futureTent)
      );

      if (pendingToSchedule <= 0) return;

      const partNoNorm = norm(row.PartNo || "");
      const isKitParent = kitParents.has(partNoNorm) || kitParents.has(norm(internalPart));

      if (poEntries.length === 0) {
        // No PO dates — show without a date
        if (!out["No Date"]) out["No Date"] = [];
        out["No Date"].push({
          rowRole: isKitParent ? "parentKit" : "directStandalone",
          parentPartNo: row.PartNo || "",
          parentDesc: row.Part_description || "",
          internalPartNo: internalPart,
          desc: row.Part_description || "",
          cust: row.Customer || "",
          poQty: totalPoQty,
          sqlRequiredQty,
          pendingToSchedule,
          futureTentative: futureTent,
          shortageQty: pendingToSchedule,
          poDate: null,
          labels: [internalPart],
          sourceRow: row,
        });
        return;
      }

      // PO walk — same algorithm as Rigid
      // demandThreshold = how much PO demand is already covered by existing stock
      // = totalPO - sqlRequiredQty
      // Walk forward, skip covered dates, place shortage on last dates
      let demandThreshold = totalPoQty - sqlRequiredQty;
      let remainingShortage = pendingToSchedule;

      for (const { date: poDate, qty: poQty } of poEntries) {
        if (remainingShortage <= 0) break;

        if (demandThreshold >= poQty) {
          // This date fully covered by stock — skip
          demandThreshold -= poQty;
        } else {
          // Shortage starts in this date
          const uncoveredInThisDate = poQty - demandThreshold;
          const shortageHere = Math.min(uncoveredInThisDate, remainingShortage);
          demandThreshold = 0;

          if (!out[poDate]) out[poDate] = [];
          out[poDate].push({
            rowRole: isKitParent ? "parentKit" : "directStandalone",
            parentPartNo: row.PartNo || "",
            parentDesc: row.Part_description || "",
            internalPartNo: internalPart,
            desc: row.Part_description || "",
            cust: row.Customer || "",
            poQty: totalPoQty,
            sqlRequiredQty,
            pendingToSchedule,
            futureTentative: futureTent,
            shortageQty: shortageHere,
            poDate,
            labels: [internalPart],
            sourceRow: row,
          });

          remainingShortage -= shortageHere;
        }
      }
    });

    return out;
  }, [postList, futureTentativeByInternal, kitParents]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 4 — BOM component rows (same logic as before, but using same PO walk)
  // ─────────────────────────────────────────────────────────────────────────────

  // Stock available per normalised internal part (from Pending_Parts_List)
  const stockByInternal = useMemo(() => {
    const out = {};
    (postList || []).forEach((row) => {
      const pendingMap = parsePendingF(row.Pending_Parts_List || "");
      Object.entries(pendingMap).forEach(([normKey, qty]) => {
        if (!out[normKey]) out[normKey] = { available: 0, rawInternal: normKey, cust: row.Customer || "" };
        out[normKey].available += qty;
      });
    });
    return out;
  }, [postList]);

  // Expand parent POs to component demand via BOM
  const componentDemandLines = useMemo(() => {
    const lines = [];
    (postList || []).forEach((row) => {
      const poEntries = parsePoDetails(row.PO_Details);
      const partNoNorm = norm(row.PartNo || "");

      // Find F internals for this row
      const pendingMap = parsePendingF(row.Pending_Parts_List || "");
      const fInternalKeys = Object.keys(pendingMap);

      const keysToCheck = [partNoNorm, ...fInternalKeys.map(norm)];

      keysToCheck.forEach((keyForBom) => {
        const rules = bomMap[keyForBom];
        if (!rules || !rules.length) return;

        poEntries.forEach(({ date, qty }) => {
          rules.forEach((rule) => {
            const wantCust = rule.customer || "";
            if (wantCust && wantCust !== norm(row.Customer || "")) return;
            const compQty = qty * rule.mult;
            if (compQty <= 0) return;
            lines.push({
              date,
              internalPartNoNorm: rule.compInternal,
              internalPartNoRaw: rule.rawCompInternal,
              qty: compQty,
              cust: row.Customer || "",
              parentPartNo: row.PartNo || "",
              parentDesc: row.Part_description || "",
            });
          });
        });
      });
    });
    return lines;
  }, [postList, bomMap]);

  const componentRowsByDate = useMemo(() => {
    const result = {};

    // Aggregate demand per date per internal
    const demandMap = {};
    componentDemandLines.forEach(({ date, internalPartNoNorm: k, qty, cust, parentDesc, internalPartNoRaw }) => {
      if (!demandMap[date]) demandMap[date] = {};
      if (!demandMap[date][k]) demandMap[date][k] = { need: 0, cust: "", parentDesc: "", rawInternal: internalPartNoRaw };
      demandMap[date][k].need += qty;
      if (!demandMap[date][k].cust && cust) demandMap[date][k].cust = cust;
      if (!demandMap[date][k].parentDesc && parentDesc) demandMap[date][k].parentDesc = parentDesc;
    });

    // Walk each component through its date timeline consuming stock
    const datesPerInternal = {};
    Object.keys(demandMap).forEach((date) => {
      Object.keys(demandMap[date]).forEach((k) => {
        (datesPerInternal[k] ??= []).push(date);
      });
    });

    const globalTotals = {};
    const perTimeline = {};

    Object.entries(datesPerInternal).forEach(([k, ds]) => {
      const uniqDates = [...new Set(ds)].sort((a, b) => a.localeCompare(b));
      const stockInfo = stockByInternal[k] || { available: 0, rawInternal: k, cust: "" };
      let available = stockInfo.available;

      uniqDates.forEach((date) => {
        const entry = demandMap[date][k];
        const need = entry.need || 0;
        const used = Math.min(need, available);
        available -= used;
        const short = Math.max(0, need - used);

        (perTimeline[k] ??= []).push({
          date,
          need,
          short,
          cust: entry.cust || stockInfo.cust || "",
          parentDesc: entry.parentDesc || "",
          rawInternal: entry.rawInternal || stockInfo.rawInternal,
        });

        if (!globalTotals[k]) globalTotals[k] = { needSum: 0, shortSum: 0 };
        globalTotals[k].needSum += need;
        globalTotals[k].shortSum += short;
      });
    });

    Object.entries(perTimeline).forEach(([k, rows]) => {
      const gNeed = globalTotals[k]?.needSum || 0;
      const gShort = globalTotals[k]?.shortSum || 0;

      rows.forEach((r) => {
        if (r.need <= 0) return;
        const futureTent = futureTentativeByInternal[k] || 0;
        const pending = Math.min(r.short, Math.max(0, r.short - futureTent));

        if (!result[r.date]) result[r.date] = [];
        result[r.date].push({
          rowRole: "componentFromBOM",
          parentDesc: r.parentDesc,
          parentPartNo: "",
          internalPartNo: r.rawInternal,
          desc: r.parentDesc,
          cust: r.cust,
          poQty: r.need,
          sqlRequiredQty: r.need,
          pendingToSchedule: pending,
          futureTentative: futureTent,
          shortageQty: pending,
          globalTotalShort: gShort,
          globalTotalNeed: gNeed,
          labels: [r.rawInternal],
        });
      });
    });

    return result;
  }, [componentDemandLines, stockByInternal, futureTentativeByInternal]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 5 — Combine direct + component rows, sort by date then role
  // ─────────────────────────────────────────────────────────────────────────────
  const byDate = useMemo(() => {
    const allDates = new Set([
      ...Object.keys(directRowsByDate),
      ...Object.keys(componentRowsByDate),
    ]);

    const roleOrder = { parentKit: 0, directStandalone: 1, componentFromBOM: 2 };
    const out = {};

    allDates.forEach((date) => {
      const rows = [
        ...(directRowsByDate[date] || []),
        ...(componentRowsByDate[date] || []),
      ].sort((a, b) => {
        const ar = roleOrder[a.rowRole] ?? 99;
        const br = roleOrder[b.rowRole] ?? 99;
        if (ar !== br) return ar - br;
        return (a.parentDesc || a.desc || "").localeCompare(b.parentDesc || b.desc || "");
      });
      if (rows.length > 0) out[date] = rows;
    });

    return out;
  }, [directRowsByDate, componentRowsByDate]);

  const dates = useMemo(
    () => Object.keys(byDate).sort((a, b) => {
      if (a === "No Date") return 1;
      if (b === "No Date") return -1;
      return a.localeCompare(b);
    }),
    [byDate]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Grand totals
  // ─────────────────────────────────────────────────────────────────────────────
  const grandTotals = useMemo(() => {
    const seenReq = new Set();
    let totalRequired = 0;
    let totalFutureTent = 0;
    let totalPending = 0;

    Object.values(byDate).forEach((rows) => {
      rows.forEach((r) => {
        if (r.rowRole === "parentKit") return; // kit parents don't schedule directly
        const key = `${r.cust}|${r.internalPartNo}`;
        if (!seenReq.has(key)) {
          seenReq.add(key);
          totalRequired += r.sqlRequiredQty || 0;
          totalFutureTent += r.futureTentative || 0;
        }
        totalPending += r.shortageQty || 0;
      });
    });

    return { totalRequired, totalFutureTent, totalPending };
  }, [byDate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Scheduling state
  // ─────────────────────────────────────────────────────────────────────────────
  const [showSchedule, setShowSchedule] = useState(false);
  const [sched, setSched] = useState({});

  const tentativeIdsRef = useRef(new WeakMap());
  const perPartCountersRef = useRef({});

  const getTentativeId = (row) => {
    const existing = tentativeIdsRef.current.get(row);
    if (existing) return existing;
    const pn = (row.internalPartNo || row.parentPartNo || "NA").trim();
    perPartCountersRef.current[pn] = (perPartCountersRef.current[pn] || 0) + 1;
    const ts = new Date().toISOString().replace(/[-:.TZ]/g, "");
    const id = `${pn}-${ts}-${perPartCountersRef.current[pn]}`;
    tentativeIdsRef.current.set(row, id);
    return id;
  };

  const setField = (key, field, value) =>
    setSched((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { employee: "", date: "", qty: "" }), [field]: value },
    }));

  const handleRowSchedule = (key, r, date) => {
    const data = sched[key] || {};
    const payload = {
      employee: (data.employee || "").trim(),
      date: (data.date || "").trim(),
      qty: Number(data.qty || 0),
      internalPartNo: r.internalPartNo || "",
      description: r.desc,
      customer: r.cust,
      poQty: Number(r.poQty || 0),
      shortage: Number(r.shortageQty || 0),
      totalRequired: Number(r.sqlRequiredQty || 0),
      poDate: date,
      partNo: r.parentPartNo || "",
      labels: r.labels || [],
      Tentative_ID: getTentativeId(r),
      type: "-F",
    };
    Axios.post(`https://${Host}/Rigid/tentative/add`, { changedData: JSON.stringify(payload), token })
      .then((res) => alert(res.data?.message || "Saved."))
      .catch((err) => alert(err?.response?.data?.message || err?.response?.data?.sqlMessage || err?.message || "Something went wrong."));
    if (onSchedule) onSchedule(payload);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-3" style={{ maxHeight: 900, overflowY: "auto" }}>

      {/* ── Header ── */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Flex -F Parts Requirements</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-primary" onClick={handlePrint}>Print</button>
          <button className="btn btn-outline-secondary" onClick={() => setShowSchedule((s) => !s)}>
            {showSchedule ? "Hide Schedule" : "Add Schedule"}
          </button>
        </div>
      </div>

      {showSchedule && employees?.length > 0 && (
        <datalist id="employees">
          {employees.map((e) => <option key={e.Emp_ID || e} value={e.Name || e} />)}
        </datalist>
      )}

      <div ref={printRef}>

        {/* ── Status banner ── */}
        {dates.length === 0 ? (
          <div className="alert alert-warning">No -F shortages found.</div>
        ) : (
          <div className="alert alert-success mb-3">
            Showing shortages across {dates.length} PO date(s).
            &nbsp;<small className="text-muted">(Tentative counted from {todayISO} onward only)</small>
          </div>
        )}

        {/* ── Per-date tables ── */}
        {dates.map((date) => {
          const rows = byDate[date];
          if (!rows || rows.length === 0) return null;
          const dateShortageTotal = rows
            .filter((r) => r.rowRole !== "parentKit")
            .reduce((s, r) => s + (r.shortageQty || 0), 0);

          return (
            <div key={date} className="mb-4">
              <h5 className="mb-2 d-flex align-items-center gap-2">
                {date === "No Date" ? "Parts without PO date" : `PO Date: ${date}`}
                {dateShortageTotal > 0 && (
                  <span className="badge bg-danger" style={{ fontSize: "0.8rem", color: "white" }}>
                    Shortage: {dateShortageTotal}
                  </span>
                )}
              </h5>

              <table className="table table-bordered table-striped table-sm">
                <thead className="table-dark">
                  <tr>
                    <th>Internal Part No</th>
                    <th>Description</th>
                    <th>Customer</th>
                    <th>PO QTY</th>
                    <th>Req -F Parts</th>
                    <th title="Future tentative schedules >= today">Fut. Tent. ↓</th>
                    <th title="Shortage on this specific PO date">Pending Sched.</th>
                    {showSchedule && (
                      <>
                        <th>Employee</th>
                        <th>Date</th>
                        <th>QTY</th>
                        <th>Schedule</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const rowKey = `${date}|${r.internalPartNo}|${i}`;
                    const rowState = sched[rowKey] || { employee: "", date: "", qty: "" };
                    const role = r.rowRole;

                    // parentKit: show PO QTY, no pending (kit parents aren't directly scheduled)
                    // directStandalone: show PO QTY + pending
                    // componentFromBOM: hide PO QTY, show pending
                    const poQtyCell = role === "componentFromBOM" ? "" : r.poQty;
                    const pendingCell = role === "parentKit" ? "" : r.shortageQty;

                    // Row styling
                    const rowStyle = role === "parentKit"
                      ? { backgroundColor: "#f8f9fa", fontStyle: "italic" }
                      : role === "componentFromBOM"
                        ? { backgroundColor: "#fff9e6" }
                        : {};

                    return (
                      <tr key={rowKey} style={rowStyle}>
                        <td title={(r.labels || []).join(", ")}>
                          {r.internalPartNo}
                          {role === "parentKit" && (
                            <span className="badge bg-secondary ms-1" style={{ fontSize: "0.65rem" }}>Kit</span>
                          )}
                        </td>
                        <td>
                          {r.desc}
                          {role === "componentFromBOM" && r.parentDesc
                            ? <small className="text-muted ms-1">(for {r.parentDesc})</small>
                            : ""}
                        </td>
                        <td>{r.cust}</td>
                        <td>{poQtyCell}</td>
                        <td><strong>{r.sqlRequiredQty}</strong></td>
                        <td>
                          {r.futureTentative > 0
                            ? <span className="badge bg-info text-dark">{r.futureTentative}</span>
                            : <span className="text-muted">0</span>}
                        </td>
                        <td>
                          {role === "parentKit" ? (
                            <span className="text-muted">—</span>
                          ) : pendingCell > 0 ? (
                            <span className="badge bg-warning text-dark fs-6">{pendingCell}</span>
                          ) : (
                            <span className="text-success fw-bold">✓ 0</span>
                          )}
                        </td>
                        {showSchedule && role !== "parentKit" && (
                          <>
                            <td style={{ minWidth: 180 }}>
                              <input className="form-control form-control-sm" placeholder="Select or type"
                                value={rowState.employee} list="employees"
                                onChange={(e) => setField(rowKey, "employee", e.target.value)} />
                            </td>
                            <td style={{ minWidth: 150 }}>
                              <input type="date" className="form-control form-control-sm"
                                value={rowState.date}
                                onChange={(e) => setField(rowKey, "date", e.target.value)} />
                            </td>
                            <td style={{ width: 110 }}>
                              <input type="number" min={0} step={1}
                                className="form-control form-control-sm"
                                value={rowState.qty}
                                onChange={(e) => setField(rowKey, "qty", e.target.value)} />
                            </td>
                            <td style={{ width: 120 }}>
                              <button className="btn btn-sm btn-success"
                                onClick={() => handleRowSchedule(rowKey, r, date)}>
                                Schedule
                              </button>
                            </td>
                          </>
                        )}
                        {showSchedule && role === "parentKit" && (
                          <td colSpan={4} className="text-muted text-center">
                            <small>Kit parent — schedule components below</small>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="table-secondary fw-bold">
                    <td colSpan={6} className="text-end">
                      Total shortage on {date === "No Date" ? "No Date" : date}:
                    </td>
                    <td>
                      <span className="badge bg-danger" style={{ fontSize: "0.8rem", color: "white" }}>{dateShortageTotal}</span>
                    </td>
                    {showSchedule && <td colSpan={4} />}
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })}

        {/* ── Grand Total ── */}
        {dates.length > 0 && (
          <div className="mb-4">
            <h5 className="mb-2">Grand Total — All -F Parts</h5>
            <table className="table table-bordered table-sm" style={{ maxWidth: 520 }}>
              <thead className="table-dark">
                <tr><th>Metric</th><th className="text-end">QTY</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Required -F Parts</td>
                  <td className="text-end fw-bold text-danger">{grandTotals.totalRequired}</td>
                </tr>
                <tr>
                  <td>Future Tentative Scheduled <small className="text-muted">(&ge; {todayISO})</small></td>
                  <td className="text-end fw-bold text-info">{grandTotals.totalFutureTent}</td>
                </tr>
                <tr className="table-warning">
                  <td>
                    <strong>Total Pending Schedule</strong>
                    <small className="text-muted ms-2">(sum of all date shortages)</small>
                  </td>
                  <td className="text-end fw-bold">{grandTotals.totalPending}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}